import cx from "classnames";
import React from "react";

import CategoryFacetOption, { CategoryKey } from "@/mk2/apps/bazaar/components/CategoryFacetOption";
import { CategoryOption } from "@/mk2/apps/bazaar/components/CategoryFacetOptions";
import CategoryFacetSubOptions from "@/mk2/apps/bazaar/components/CategoryFacetSubOptions";
import { getLastRowIndexStart } from "@/mk2/helpers/grid";

import styles from "./CategoryFacetOptionsSection.mscss";

const ROW_LENGTH = 4;
const ROW_LENGTH_MOBILE = 2;

export interface OwnProps {
    className?: string;
    label: React.ReactNode;
    options?: CategoryOption[];
    selected?: CategoryKey;
    opened?: CategoryKey;

    onSelect?(category: CategoryKey);
    onToggle?(category: CategoryKey);
}

type Props = OwnProps;

const childMatch = (s, key): boolean =>
    s.key === key || s.children?.some((c) => childMatch(c, key));

const CategoryFacetOptionsSection: React.FunctionComponent<Props> = ({
    className,
    label,
    options,
    selected = null,
    opened,
    onSelect,
    onToggle,
}) => {
    const toFill = (ROW_LENGTH - (options.length % ROW_LENGTH)) % ROW_LENGTH;
    const openedIndex = options.findIndex(
        (o) => o.key === opened || o.children?.some((c) => childMatch(c, opened)),
    );
    const openedOption = options[openedIndex];
    const openedDesktopIndex =
        openedIndex > -1 && Math.ceil((openedIndex + 1) / ROW_LENGTH) * ROW_LENGTH;
    const openedMobileIndex =
        openedIndex > -1 && Math.ceil((openedIndex + 1) / ROW_LENGTH_MOBILE) * ROW_LENGTH_MOBILE;

    const lastRowStartDesktopIndex = getLastRowIndexStart(options, ROW_LENGTH);
    const lastRowStartMobileIndex = getLastRowIndexStart(options, ROW_LENGTH_MOBILE);

    return (
        <div className={cx(styles.CategoryFacetOptionsSection, className)}>
            {options.map((opt, index) => (
                <React.Fragment key={opt.key}>
                    {(index === openedDesktopIndex || index === openedMobileIndex) && (
                        <div
                            className={
                                styles[
                                    `CategoryFacetOptionsSection__suboptions--${
                                        openedDesktopIndex === openedMobileIndex
                                            ? ""
                                            : index === openedDesktopIndex
                                              ? "desktop"
                                              : "mobile"
                                    }`
                                ]
                            }
                        >
                            <CategoryFacetSubOptions
                                options={openedOption.children}
                                opened={opened}
                                onSelect={onSelect}
                                selected={selected}
                                onToggle={onToggle}
                            />
                        </div>
                    )}
                    <CategoryFacetOption
                        className={cx(
                            styles.CategoryFacetOptionsSection__option,
                            index < ROW_LENGTH &&
                                styles["CategoryFacetOptionsSection__row_first--desktop"],
                            index < ROW_LENGTH_MOBILE &&
                                styles["CategoryFacetOptionsSection__row_first--mobile"],
                            styles[
                                `CategoryFacetOptionsSection__column_${(index % ROW_LENGTH) + 1}--desktop`
                            ],
                            styles[
                                `CategoryFacetOptionsSection__column_${(index % ROW_LENGTH_MOBILE) + 1}--mobile`
                            ],
                            index >= lastRowStartDesktopIndex &&
                                styles[`CategoryFacetOptionsSection__row_last--desktop`],
                            index >= lastRowStartMobileIndex &&
                                styles[`CategoryFacetOptionsSection__row_last--mobile`],
                        )}
                        key={opt.key}
                        category={opt.key}
                        count={opt.count}
                        to={opt.to}
                        selected={
                            selected === opt.key ||
                            opt.children?.some((s) => childMatch(s, selected))
                        }
                        hasSubcategories={opt.children?.length > 0}
                        dimmed={(opened && !openedOption) || (openedOption && openedOption !== opt)}
                        opened={openedOption === opt}
                        onSelect={opt.children?.length > 0 ? onToggle : onSelect}
                    />
                </React.Fragment>
            ))}
            {Array.from(Array(toFill).keys()).map((index) => (
                <div
                    key={`empty-${index}`}
                    className={cx(
                        styles.CategoryFacetOptionsSection__empty,
                        styles[
                            `CategoryFacetOptionsSection__column_${
                                ((options.length + index) % ROW_LENGTH) + 1
                            }--desktop`
                        ],
                        styles[`CategoryFacetOptionsSection__row_last--desktop`],
                        styles[
                            `CategoryFacetOptionsSection__column_${
                                ((options.length + index) % ROW_LENGTH_MOBILE) + 1
                            }--mobile`
                        ],
                        index === 0 &&
                            (toFill === 1 || toFill === 3) &&
                            styles[`CategoryFacetOptionsSection__row_last--mobile`], // on mobile we can have max 1 empty placeholder
                        (index !== 0 || toFill === 2) &&
                            styles[`CategoryFacetOptionsSection__row_last--mobile--hide`], // on mobile we can have max 1 empty placeholder
                    )}
                />
            ))}
            {(options.length <= openedDesktopIndex || options.length <= openedMobileIndex) && (
                <div
                    className={
                        styles[
                            `CategoryFacetOptionsSection__suboptions--${
                                options.length <= openedMobileIndex
                                    ? "" // Display on both
                                    : "desktop" // Display only on desktop
                            }`
                        ]
                    }
                >
                    <CategoryFacetSubOptions
                        options={openedOption.children}
                        opened={opened}
                        onSelect={onSelect}
                        selected={selected}
                        onToggle={onToggle}
                    />
                </div>
            )}
        </div>
    );
};

CategoryFacetOptionsSection.displayName = "CategoryFacetOptionsSection";

export default React.memo<Props>(CategoryFacetOptionsSection);
